import * as yup from "yup";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import { toØre } from "@chef/utils/currency";
import {
  FlexWrapper,
  PageLoader,
  PortionsMealsSelect,
  PreferenceSelectList,
  TastePreferencesNotification,
  CurrencyWrapper as Currency,
} from "@chef/smart-components";
import { AppShell } from "@chef/feature-app-shell";
import {
  getPartnership,
  selectSignupAddonSubscriptions,
  selectSignupAffiliateIds,
  selectSignupExternalIdentifiers,
  selectSignupWeekAndYear,
  usePreferencesQuery,
  useProductsByIdsQuery,
} from "@chef/state-management";
import {
  BRAND_NAME,
  BRAND_PATHS,
  CONSENT_IDS,
  CURRENCY,
  getDishCount,
  order,
  PARTNER_IDS,
  PREFERENCE_IDS,
  PRODUCT_IDS,
} from "@chef/constants";
import {
  getSelectedPreferenceDescription,
  isOneSubProduct,
  isPreferencesAllergen,
  isSingelPreference,
} from "@chef/helpers";
import {
  getAttribute,
  getDiscountPrice,
  getMealsFromVariations,
  getPortionsFromVariations,
  getPriceOfProductByVariation,
} from "@chef/state-management/helpers";
import {
  useAppSelector,
  usePreferences,
  useTrack,
} from "@chef/state-management/hooks";
import { useEffectOnce, useIntercom } from "@chef/hooks";

import { isEmptyArray, isNotEmptyArray } from "@chef/utils/array";
import { ChatBox, Snackbar } from "@chef/components";

import { BlockContent, ISection } from "@chef/feature-sanity";
import { isEqualStrings } from "@chef/utils/equal";

import type { SignupSession } from "@chef/types/payment";

import { Header } from "../components/Header";
import { useSignupSession } from "../hooks/useSignupSession";
import { DiscountCodeInput } from "../components/DiscountCodeInput";
import { useTrackingData } from "../hooks/useTrackingData";
import { MealboxSummary } from "../components/MealboxSummary";
import {
  getIdsFromSlugs,
  getSlugsFromIds,
  getValidMeal,
  getValidPortion,
  useSearchParams,
} from "../utils";

import { intl } from "./SignupSelectPreferences.Intl";

interface SignupSelectPreferencesProps {
  sanityBanner?: ISection | null;
}

const schema = yup.object({
  conceptPreferenceIds: yup.array().of(yup.string().defined()).compact(),
  tastePreferenceIds: yup.array().of(yup.string().defined()).compact(),
  portions: yup.number().required(),
  meals: yup.number().required(),
});

type PreferencesFormData = yup.InferType<typeof schema>;
type ErrorResponse = { data: { redirectUri: string } };

export const SignupSelectPreferences = ({
  sanityBanner,
}: SignupSelectPreferencesProps) => {
  useIntercom(false);

  const router = useRouter();

  const track = useTrack();

  const { data: preferencesQuery } = usePreferencesQuery();

  const { week, year } = useSelector(selectSignupWeekAndYear);

  const { coupon, discount, products } = useTrackingData();

  const { updateSignupSession, isUpdatingSignupSession } = useSignupSession();

  const { data: productsByIdsQuery } = useProductsByIdsQuery(
    {
      productIds: [PRODUCT_IDS.ONESUB],
      week,
      year,
    },
    { skip: !week || !year },
  );

  const couponDetails = useAppSelector((state) => state.signupRtk.coupon);

  const affiliateIds = useSelector(selectSignupAffiliateIds);
  const partnerships = useAppSelector((state) => state.signupRtk.partnerships);
  const addonSubscriptionIds = useSelector(selectSignupAddonSubscriptions);
  const externalIdentifiers = useSelector(selectSignupExternalIdentifiers);

  const eurobonusPartnership = getPartnership(
    partnerships,
    PARTNER_IDS.EUROBONUS,
  );

  const getSignupCoupon = () => {
    if (eurobonusPartnership && couponDetails?.source === "default") {
      return { couponCode: undefined, couponDetails: undefined };
    }

    return {
      couponCode: coupon ?? undefined,
      couponDetails: couponDetails ?? undefined,
    };
  };

  const signupCoupon = getSignupCoupon();

  const {
    conceptPreferences,
    tastePreferences,
    fallbackPreferenceId,
    fixedPreferences,
    getSelectedPreferenceDetails,
  } = usePreferences();

  const { state: queryParams, updateParams } = useSearchParams({
    [intl.ROUTE_PORTIONS]: order.defaultPortions,
    [intl.ROUTE_MEALS]: order.defaultMeals,
    [intl.ROUTE_PREFERENCE]: [],
    [intl.ROUTE_TASTE]: [],
  });

  const _tastePreferenceIds = getIdsFromSlugs(
    queryParams[intl.ROUTE_TASTE] as string | string[],
    tastePreferences,
  );
  const _conceptPreferenceIds = getIdsFromSlugs(
    queryParams[intl.ROUTE_PREFERENCE] as string | string[],
    [...conceptPreferences, ...fixedPreferences],
  );

  const oneSubProduct = productsByIdsQuery?.products.find(isOneSubProduct);

  const portionOptions = getPortionsFromVariations({
    variations: oneSubProduct?.variations,
  });

  const mealsOptions = getMealsFromVariations({
    variations: oneSubProduct?.variations,
    portions: +queryParams[intl.ROUTE_PORTIONS],
  });

  const _portions = getValidPortion(
    queryParams[intl.ROUTE_PORTIONS],
    portionOptions,
    order.defaultPortions,
  );

  const _meals = getValidMeal(
    queryParams[intl.ROUTE_MEALS],
    mealsOptions,
    order.defaultMeals,
  );

  const methods = useForm<PreferencesFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: useMemo(() => {
      return {
        conceptPreferenceIds: _conceptPreferenceIds,
        tastePreferenceIds: _tastePreferenceIds,
        portions: _portions,
        meals: _meals,
      };
    }, [_conceptPreferenceIds, _tastePreferenceIds, _portions, _meals]),
  });

  const [
    selectedConceptPreferenceIds,
    selectedTastePreferenceIds,
    selectedPortions,
    selectedMeals,
  ] = methods.watch([
    "conceptPreferenceIds",
    "tastePreferenceIds",
    "portions",
    "meals",
  ]);

  const syncFormToUrl = useCallback(
    (values: PreferencesFormData) => {
      const currentParams = new URLSearchParams(window.location.search);

      const conceptSlugs = getSlugsFromIds(values.conceptPreferenceIds, [
        ...conceptPreferences,
        ...fixedPreferences,
      ]);
      const tasteSlugs = getSlugsFromIds(
        values.tastePreferenceIds,
        tastePreferences,
      );

      const updates: Record<string, string | string[] | number> = {
        [intl.ROUTE_MEALS]: values.meals,
        [intl.ROUTE_PORTIONS]: values.portions,
        [intl.ROUTE_PREFERENCE]: conceptSlugs,
        [intl.ROUTE_TASTE]: tasteSlugs,
      };

      let hasChanges = false;

      Object.entries(updates).forEach(([key, value]) => {
        const newValue = Array.isArray(value)
          ? value.join("-")
          : value.toString();
        if (currentParams.get(key) !== newValue) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        updateParams(updates, [intl.ROUTE_MEALBOX]);
      }
    },
    [conceptPreferences, tastePreferences, updateParams, fixedPreferences],
  );

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);

      methods.setValue(
        "conceptPreferenceIds",
        isEmptyArray(_conceptPreferenceIds)
          ? [fallbackPreferenceId || ""]
          : _conceptPreferenceIds,
      );
      methods.setValue("tastePreferenceIds", _tastePreferenceIds);
      methods.setValue("portions", _portions);
      methods.setValue("meals", _meals);

      return; // Skip sync on first render
    }

    const values = methods.getValues();

    syncFormToUrl(values);
  }, [
    methods,
    syncFormToUrl,
    initialized,
    _conceptPreferenceIds,
    _tastePreferenceIds,
    _portions,
    _meals,
    fallbackPreferenceId,
    updateParams,
  ]);

  useEffectOnce(() => {
    track("signupStarted", {
      affiliation: "Frontend process",
      currency: CURRENCY,
      discount,
      products,
    });
  }, products !== undefined);

  if (!preferencesQuery || !productsByIdsQuery) {
    return <PageLoader wholeScreen />;
  }

  const singlePortionSelected = selectedPortions === 1;

  const selectedVariation = oneSubProduct?.variations.find(
    (v) =>
      isEqualStrings(
        getAttribute("Portions", v),
        selectedPortions?.toString(),
      ) && isEqualStrings(getAttribute("Meals", v), selectedMeals?.toString()),
  );

  const {
    selectedFixedPreferenceIds,
    disableConceptPreferences,
    canSeeTastePreferences,
  } = getSelectedPreferenceDetails({
    selectedPreferenceIds: selectedConceptPreferenceIds as string[],
  });

  const onSubmit = async (data: PreferencesFormData) => {
    track("productSelected", {
      affiliation: "Frontend process",
      coupon: signupCoupon.couponCode,
      discount,
      currency: CURRENCY,
      products,
    });

    if (!oneSubProduct || !selectedVariation) {
      // TODO: do we need to do anything else than return here?
      return;
    }

    let _conceptPreferenceIds = data.conceptPreferenceIds || [];
    let _tastePreferenceIds = data.tastePreferenceIds || [];

    if (isNotEmptyArray(selectedFixedPreferenceIds)) {
      _conceptPreferenceIds = [...selectedFixedPreferenceIds];
      _tastePreferenceIds = [];
    }

    if (!canSeeTastePreferences) {
      _tastePreferenceIds = [];
    }

    if (data.portions === 1) {
      _conceptPreferenceIds = [PREFERENCE_IDS.SINGEL];
      _tastePreferenceIds = [];
    } else {
      _conceptPreferenceIds = _conceptPreferenceIds.filter(
        (id) => !isSingelPreference({ preferenceId: id }),
      );
    }

    if (isEmptyArray(_conceptPreferenceIds)) {
      _conceptPreferenceIds = [fallbackPreferenceId];
    }

    const sessionData = {
      basket: {
        items: [
          {
            productId: oneSubProduct.productId,
            productTypeId: oneSubProduct.productTypeId,
            variationId: selectedVariation.variationId,
            name: selectedVariation.name,
            quantity: 1,
            price: toØre(selectedVariation.finalPrice),
          },
        ],
        coupon: signupCoupon.couponCode,

        giftcard: null,

        _meta: {
          week,
          year,
        },
      },

      addonSubscriptionIds: [] as string[],

      marketing: {},

      preferencesIds: [..._conceptPreferenceIds, ..._tastePreferenceIds],

      consents: [
        {
          consentId: CONSENT_IDS.DATA_PROCESSING[BRAND_NAME],
          isAccepted: true,
        },
      ],
    } as SignupSession;

    const selectedAllergen = isPreferencesAllergen(
      sessionData.preferencesIds,
      tastePreferences,
    );

    if (selectedAllergen) {
      sessionData.consents.push({
        consentId: CONSENT_IDS.ALLERGEN_STORING[BRAND_NAME],
        isAccepted: true,
      });
    }

    if (couponDetails?.salesPointId) {
      sessionData.marketing.salesPointId = couponDetails.salesPointId;
    }

    if (affiliateIds?.at) {
      sessionData.marketing.affiliateIds = [affiliateIds.at];
    }

    if (isNotEmptyArray(partnerships)) {
      sessionData.marketing.partnerships = partnerships;
    }

    if (addonSubscriptionIds) {
      sessionData.addonSubscriptionIds = addonSubscriptionIds;
    }

    if (externalIdentifiers) {
      sessionData.externalIdentifiers = externalIdentifiers;
    }

    // Update remote session data
    try {
      await updateSignupSession(sessionData).unwrap();
      router.push(BRAND_PATHS.SIGNUP_CREATE_ACCOUNT_HREF);
    } catch (error) {
      const err = error as ErrorResponse;
      if (err.data.redirectUri) {
        // To make sure customers keep their meals and portions we wil push to the current path instead of using the redirectUri
        // because the redirectUri will not contain the meals and portions query
        const currentPath = router.asPath;
        window.location.href = currentPath;
      }
    }
  };

  const variationPrice =
    (selectedVariation && getPriceOfProductByVariation(selectedVariation)) || 0;

  const regularPrice = variationPrice;

  const { flexiblePriceWithDiscount, isPriceDiscounted } = getDiscountPrice({
    type: "signup",
    variationPrice,
    couponDetails: signupCoupon.couponDetails,
  });

  const dishCount = getDishCount({ week, year, portions: selectedPortions });

  const allergenSelected = isPreferencesAllergen(
    selectedTastePreferenceIds,
    tastePreferences,
  );

  return (
    <AppShell title={intl.DESIGN_YOUR_MEALKIT}>
      <AppShell.Meta />
      <AppShell.Drawer />

      <Header step={1} />

      <AppShell.DiscountBanner cta="discount-scroll" />

      {sanityBanner && (
        <div className="absolute z-0">
          <BlockContent
            body={sanityBanner.content.body}
            prose={{ padding: "none" }}
          />
        </div>
      )}

      <AppShell.Main grid noPadding gradient>
        <FlexWrapper
          background="white"
          back={{ type: "hidden" }}
          close={{ type: "hidden" }}
          className="md:mb-8 md:mt-16"
        >
          <>
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className="flex flex-col"
              >
                <div className="flex flex-col gap-10 p-4 text-center">
                  <section id="mealbox-selection">
                    <div className="py-8">
                      <h1 className="mb-6 text-xl">
                        <strong>{intl.DESIGN_YOUR_MEALKIT}</strong>
                      </h1>
                      <p className="pt-2">
                        <strong>{intl.GET_EXCITED}</strong> -{" "}
                        {intl.YOUR_CHOICE_OF_XX_WEEKLY_DISHES_IS_JUST_A_FEW_STEPS_AWAY(
                          getDishCount({
                            week,
                            year,
                            portions: selectedPortions,
                          }),
                        )}
                      </p>
                    </div>
                    <h1 className="mb-6 text-xl">
                      <strong>{intl.SELECT_SIZE}</strong>
                    </h1>
                    <PortionsMealsSelect mealboxProduct={oneSubProduct} />
                  </section>

                  <hr className="border-1.5 text-grey-3 my-2" />

                  {singlePortionSelected && (
                    <Snackbar nudge className="text-left" id="pro-tip-snackbar">
                      <p className="text-sm">
                        <strong>{intl.PRO_TIP}</strong>{" "}
                        {intl.IF_YOU_WANT_TO_CHOOSE_FULL_RANGE}
                      </p>
                    </Snackbar>
                  )}
                  {!singlePortionSelected && (
                    <>
                      <section id="concept-preference-selection">
                        <h1 className="text-xl">
                          <strong>{intl.WHAT_DISHES_DO_YOU_LIKE}</strong>
                        </h1>
                        <p className="mt-2 text-sm">
                          {intl.FULL_MENU_IS_ALWAYS_AVAILABLE}
                        </p>
                        <PreferenceSelectList
                          preferences={conceptPreferences}
                          control={methods.control}
                          name="conceptPreferenceIds"
                          className={clsx(
                            disableConceptPreferences && "opacity-30",
                            "flex flex-col gap-3 lg:grid lg:grid-cols-2 mt-6",
                          )}
                          disabled={
                            disableConceptPreferences ||
                            methods.formState.isSubmitting
                          }
                          portions={selectedPortions}
                        />
                        <p className="px-1 pt-4 text-sm">
                          {getSelectedPreferenceDescription({
                            preferenceIds:
                              selectedConceptPreferenceIds as string[],
                            preferences: preferencesQuery.preferences,
                          })}
                        </p>
                      </section>

                      {isNotEmptyArray(fixedPreferences) && (
                        <section id="fixed-preference-selection">
                          <strong className="text-base">
                            {intl.OR_SELECT_FIXED}
                          </strong>

                          <PreferenceSelectList
                            preferences={fixedPreferences}
                            name="conceptPreferenceIds"
                            control={methods.control}
                            className="grid grid-cols-1 gap-3 mt-5"
                            disabled={methods.formState.isSubmitting}
                            isFixed
                            portions={selectedPortions}
                          />

                          {isNotEmptyArray(selectedFixedPreferenceIds) && (
                            <ChatBox className="text-sm bg-informationBG">
                              {intl.YOU_HAVE_SELECTED_FIXED}
                            </ChatBox>
                          )}
                        </section>
                      )}

                      <hr className="border-1.5 text-grey-3 my-2" />
                      <section
                        id="taste-preference-selection"
                        className="flex flex-col gap-2"
                      >
                        <h1 className="text-xl">
                          <strong>
                            {intl.INGREDIENTS_YOU}{" "}
                            <span className="px-2 rounded bg-errorBG">
                              {intl.DO_NOT}
                            </span>{" "}
                            {intl.WANT}
                            <div>({intl.OPTIONAL})</div>
                          </strong>
                        </h1>

                        {canSeeTastePreferences && (
                          <>
                            <p className="text-sm">
                              {
                                intl.WE_WILL_DO_OUR_BEST_TO_AVOID_THESE_INGREDIENTS
                              }
                            </p>
                            <PreferenceSelectList
                              preferences={tastePreferences}
                              control={methods.control}
                              name="tastePreferenceIds"
                              className="grid grid-cols-2 gap-3 mt-5 lg:grid-cols-3"
                              disabled={methods.formState.isSubmitting}
                              portions={selectedPortions}
                            />

                            <TastePreferencesNotification
                              tastePreferenceIds={
                                selectedTastePreferenceIds as string[]
                              }
                              conceptPreferenceIds={
                                selectedConceptPreferenceIds as string[]
                              }
                            />
                          </>
                        )}
                        {!canSeeTastePreferences && (
                          <p className="border-1.5 rounded p-4 border-grey-2">
                            {intl.CANNOT_DESELECT_INGREDIENTS}
                          </p>
                        )}
                      </section>

                      <div className="text-sm text-grey-1">
                        {allergenSelected ? (
                          intl.BY_SELECTING_ALLERGENS_YOU_APPROVE_THAT_WE_CAN_STORE_YOUR_ALLERGIES
                        ) : (
                          <>
                            <strong>
                              {intl.IS_THERE_ANYTHING_ELSE_YOU_DO_NOT_WANT}
                            </strong>
                            <p className="lg:text-xs">
                              {intl.MAKE_SURE_TO_READ_RECIPES}
                            </p>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  <div
                    className="w-full my-6 text-center md:hidden"
                    id="discount-card"
                  >
                    <DiscountCodeInput hideDetails={true} />
                  </div>

                  <hr className="hidden md:block border-1.5 text-grey-3 my-2" />
                </div>

                <MealboxSummary isSubmitting={isUpdatingSignupSession} />
              </form>
            </FormProvider>

            <div>
              <div>
                <div className="w-full h-16 pt-2 mx-auto text-center text-white bg-black rounded-b-lg">
                  <div>
                    {intl.FOOD_FOR_N_PEOPLE(selectedPortions)},{" "}
                    {intl.FOOD_FOR_N_DISHES_PER_WEEK(selectedMeals)}
                  </div>

                  <span className="flex justify-center gap-4">
                    {isPriceDiscounted && (
                      <Currency className="line-through text-grey-1">
                        {regularPrice}
                      </Currency>
                    )}
                    <Currency
                      className={clsx(isPriceDiscounted && "text-white")}
                    >
                      {flexiblePriceWithDiscount}
                    </Currency>
                  </span>
                </div>
              </div>
            </div>
          </>
        </FlexWrapper>

        <FlexWrapper
          background="grey"
          back={{ type: "hidden" }}
          close={{ type: "hidden" }}
          className="hidden my-10 md:block"
        >
          <div className="p-10" id="discount-card">
            <h1 className="pb-2 text-lg text-center">
              <strong>
                {intl.CHOOSE_BETWEEN_XX_DISHES_EVERY_WEEK(dishCount)}
              </strong>
            </h1>
            <p className="pb-4 text-sm text-center">
              {
                intl.CREATE_YOUR_ACCOUNT_TO_SEE_YOUR_FULL_MENU_AND_IF_YOU_WANT_SWAP_YOUR_MENU
              }
            </p>

            <DiscountCodeInput hideDetails={true} />
          </div>
        </FlexWrapper>
      </AppShell.Main>

      <AppShell.Footer variant="minified" hideAppStoreBanner />

      <AppShell.Sticky anchor="bottom">
        <AppShell.TabBar />
      </AppShell.Sticky>
    </AppShell>
  );
};
